import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Shield, QrCode, CheckCircle, AlertTriangle, Trash2, KeyRound } from "lucide-react";

export default function TwoFactorSetup() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null); // { enabled: bool }
  const [setupData, setSetupData] = useState(null); // { qr_b64, provisioning_uri, secret }
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle"); // "idle" | "setup" | "disable"
  const [disableCode, setDisableCode] = useState("");

  useEffect(() => {
    api.get("/auth/2fa/status").then(r => setStatus(r.data));
  }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setPhase("setup");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start 2FA setup");
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.length < 6) return;
    setLoading(true);
    try {
      await api.post("/auth/2fa/verify-setup", { code });
      toast.success("2FA enabled successfully!");
      setStatus({ enabled: true });
      setPhase("idle");
      setSetupData(null);
      setCode("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code — try again");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const startDisable = () => setPhase("disable");

  const confirmDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("2FA disabled.");
      setStatus({ enabled: false });
      setPhase("idle");
      setDisableCode("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
      setDisableCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#1a1a2e] border border-[#B8A4FF]/20 flex items-center justify-center">
            <Shield size={18} className="text-[#B8A4FF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Two-Factor Authentication</h1>
            <p className="text-[13px] text-[#B0AEBC]">Add an extra layer of security to your account</p>
          </div>
        </div>

        {/* Status card */}
        <div className={`card p-5 mb-6 border ${status?.enabled ? "border-green-400/30" : "border-[#2a2a3e]"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {status?.enabled
                ? <CheckCircle size={20} className="text-green-400" />
                : <AlertTriangle size={20} className="text-yellow-400" />
              }
              <div>
                <p className="font-semibold text-white">
                  {status?.enabled ? "2FA is enabled" : "2FA is not enabled"}
                </p>
                <p className="text-[12px] text-[#B0AEBC] mt-0.5">
                  {status?.enabled
                    ? "Your account is protected with TOTP authentication."
                    : "Enable 2FA to protect your account with Google Authenticator or Authy."}
                </p>
              </div>
            </div>
            {status !== null && (
              <div>
                {!status.enabled && phase === "idle" && (
                  <button onClick={startSetup} disabled={loading}
                    className="btn btn-primary px-4 py-2 text-[13px]" data-testid="twofa-enable-btn">
                    Enable 2FA
                  </button>
                )}
                {status.enabled && phase === "idle" && (
                  <button onClick={startDisable}
                    className="btn btn-outline px-4 py-2 text-[13px] border-red-400/40 text-red-400 hover:bg-red-400/10"
                    data-testid="twofa-disable-btn">
                    <Trash2 size={13} className="inline mr-1" /> Disable
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Setup phase */}
        {phase === "setup" && setupData && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card p-6 space-y-5">
            <div>
              <h2 className="text-[16px] font-semibold text-white mb-1 flex items-center gap-2">
                <QrCode size={16} className="text-[#B8A4FF]" /> Scan QR Code
              </h2>
              <p className="text-[13px] text-[#B0AEBC]">
                Open <strong className="text-white">Google Authenticator</strong> or <strong className="text-white">Authy</strong> and scan this code.
              </p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center p-4 bg-white rounded-xl w-fit mx-auto">
              <img
                src={`data:image/png;base64,${setupData.qr_b64}`}
                alt="TOTP QR code"
                className="w-48 h-48"
                data-testid="twofa-qr-image"
              />
            </div>

            {/* Manual entry */}
            <div className="bg-[#131316] rounded-lg p-3 border border-[#2a2a3e]">
              <p className="text-[11px] text-[#B0AEBC] mb-1">Can't scan? Enter this code manually:</p>
              <p className="font-mono text-[14px] text-[#B8A4FF] break-all">{setupData.secret}</p>
            </div>

            <form onSubmit={verifySetup} className="space-y-3">
              <div>
                <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">
                  Enter the 6-digit code from your app to confirm
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                  className="input text-center text-2xl tracking-[0.4em] font-mono"
                  placeholder="000000"
                  data-testid="twofa-verify-code-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={loading || code.length !== 6}
                  className="btn btn-primary flex-1 py-2.5 text-[14px]" data-testid="twofa-verify-btn">
                  {loading ? "Verifying…" : <><KeyRound size={14} className="inline mr-1" /> Verify & Enable</>}
                </button>
                <button type="button" onClick={() => { setPhase("idle"); setSetupData(null); setCode(""); }}
                  className="btn btn-outline px-4 py-2.5 text-[13px]">
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Disable phase */}
        {phase === "disable" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card p-6 space-y-4 border border-red-400/20">
            <p className="text-[14px] text-[#D4D4D8]">
              Enter your current 2FA code to confirm you want to disable two-factor authentication.
            </p>
            <form onSubmit={confirmDisable} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={disableCode}
                onChange={e => setDisableCode(e.target.value.replace(/\D/g, ""))}
                className="input text-center text-2xl tracking-[0.4em] font-mono w-full"
                placeholder="000000"
                autoFocus
              />
              <div className="flex gap-3">
                <button type="submit" disabled={loading || disableCode.length !== 6}
                  className="btn flex-1 py-2.5 text-[14px] bg-red-500/10 text-red-400 border border-red-400/30 hover:bg-red-400/20">
                  {loading ? "Disabling…" : "Disable 2FA"}
                </button>
                <button type="button" onClick={() => { setPhase("idle"); setDisableCode(""); }}
                  className="btn btn-outline px-4 py-2.5 text-[13px]">
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Info */}
        <div className="card p-4 mt-6 border-[#2a2a3e]">
          <h3 className="text-[13px] font-semibold text-white mb-2">About 2FA</h3>
          <ul className="space-y-1 text-[12px] text-[#B0AEBC]">
            <li>• Uses TOTP (Time-based One-Time Passwords) — industry standard</li>
            <li>• Compatible with Google Authenticator, Authy, 1Password, Bitwarden</li>
            <li>• Codes refresh every 30 seconds</li>
            <li>• If you lose access to your authenticator app, contact support</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
